import { memo, useCallback } from "react";
import { intl } from "LocaleWrapper";
import { useApolloClient } from "@apollo/client";

import { Checkbox, Input, Select } from "@cofano/cck";

import CharterSubtypeSelect from "view/charters/components/selects/CharterSubtypeSelect";
import { AdminSettingsPlugin, AdminSettingsPluginValue, PluginType } from "view/admin/utils/admin.types";

import { AgreementTypeEnum, CharterSubtype } from "__generated__/enums";
import { GetContainerTypesDocument } from "__generated__/view/containertypes/graphql/containerType.queries.generated";

interface AdminSettingsPluginFieldProps {
	plugin: AdminSettingsPlugin;
	onPluginChange: (pluginKey: string, value: AdminSettingsPluginValue) => void;
}

const AdminSettingsPluginField = memo(({ plugin, onPluginChange }: AdminSettingsPluginFieldProps) => {

	const client = useApolloClient();

	const { value, allowedValues, typeId, key } = plugin;

	const fetchContainerTypes = useCallback(async (inputValue: string) => {
		const result = await client.query({
			query: GetContainerTypesDocument,
			variables: { type: inputValue, includeTeu: false },
		});

		return (result.data?.containerTypes ?? []).map((val) => ({
			...val,
			id: val.isoTypeCode,
		}));
	}, [ client ]);

	if (typeId === PluginType.Boolean) {
		const isChecked = value === "true" || value === "1";

		return (
			<Checkbox
				checked={isChecked}
				onChange={(event) => {
					const stringValue = event.target.checked ? "true" : "false";
					onPluginChange(key, stringValue);
				}}
				color="primary"
			/>
		);
	}

	if (typeId === PluginType.String || typeId === PluginType.MultiValue) {

		if (key === "customCharterSubtypes") {
			const options = Object.values(CharterSubtype).map((val) => ({
				id: val,
				name: intl.formatMessage({ id: val }),
			}));

			return (
				<Select
					onChange={(option) => onPluginChange(key, option ? option.map((it) => it?.id).join(";") : undefined)}
					value={value ? value.split(";").map((id) => options.find((it) => it.id === id)).filter((it) => !!it) : undefined}
					fetchOptions={options}
					disableSearch
					multiple />
			);
		}

		if (key === "customAgreementTypes") {
			const options = Object.values(AgreementTypeEnum).map((val) => ({
				id: val,
				name: intl.formatMessage({ id: `AGREEMENT_TYPE_${val}` }),
			}));

			return (
				<Select
					onChange={(option) => onPluginChange(key, option ? option.map((it) => it?.id).join(";") : undefined)}
					value={value ? value.split(";").map((id) => options.find((it) => it.id === id)).filter((it) => !!it) : undefined}
					fetchOptions={options}
					disableSearch
					multiple />
			);
		}

		if (key === "customContainerTypes") {
			return (
				<Select
					onChange={(options) => onPluginChange(key, options ? options.map((it) => it?.id).join(";") : undefined)}
					value={value ? value.split(";").map((id) => ({ isoTypeCode: id, id })) : []}
					fetchOptions={fetchContainerTypes}
					getOptionLabel={(option) => option?.isoTypeCode ?? ""}
					multiple />
			);
		}

		if (key === "defaultSalesCharterSubtype") {
			return (
				<CharterSubtypeSelect
					value={value as CharterSubtype}
					onChange={(newValue) => onPluginChange(key, newValue)}
					customCharterSubtypes={[ CharterSubtype.Spot, CharterSubtype.Contract, CharterSubtype.Time ]} />
			);
		}

		return (
			<Input
				value={value ?? ""}
				onChange={(event) => onPluginChange(key, event.target.value)} />
		);
	}


	if (typeId === PluginType.Number) {
		return (
			<Input
				value={value ?? ""}
				onChange={(event) => {
					let inputValue = event.target.value;
					inputValue = inputValue.replace(/,/, ".");
					if (inputValue && isNaN(Number(inputValue))) {
						return;
					}
					onPluginChange(key, Number(inputValue));
				}} />
		);
	}

	if (typeId === PluginType.Select) {
		const options = (allowedValues ?? []).map((name) => ({
			id: name,
			name: name,
		}));

		return (
			<Select
				onChange={(option) => onPluginChange(key, option?.id)}
				value={value ? options.find((option) => option.id === value) : undefined}
				fetchOptions={options} />
		);
	}

	if (typeId === PluginType.Double) {
		return (
			<Input
				type="number"
				value={value ?? ""}
				onChange={(event) => {
					const inputValue = event.target.value;
					if (inputValue && isNaN(Number(inputValue))) {
						return;
					}
					onPluginChange(key, inputValue ? Number(inputValue) : undefined);
				}}
			/>
		);
	}

	return null;
});

export default AdminSettingsPluginField;
